/**
 * Legislation Documents tab pieces (screenshots/38, 41 card 11):
 *  - DocPreview: the selected PDF drawn with pdf.js, "Find in document" (⌘K) highlights the matching lines
 *    and ↑ ↓ jump between matches; download + City Clerk original.
 *  - ComparePanel: Original vs. Substitute side by side, synced scroll, removed text red with a
 *    strikethrough, added text green, a minimap of where changes cluster, n / p jump (with a flash).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, ExternalLink, Search, X, ZoomIn } from "lucide-react";
import { prefersReducedMotion, recipes } from "../motion";
import { isTyping } from "../hooks/useTabs";
import type { LegislationDocument } from "./types";

type PdfDoc = import("pdfjs-dist").PDFDocumentProxy;
let pdfjs: Promise<typeof import("pdfjs-dist")> | null = null;
function loadPdfjs() {
  pdfjs ??= import("pdfjs-dist").then((m) => {
    m.GlobalWorkerOptions.workerSrc = new URL("./pdf.worker.min.mjs", import.meta.url).href;
    return m;
  });
  return pdfjs;
}
const PAGE_W = 400;
const ZOOMS = [1, 1.25, 1.5];

interface Hit { page: number; x: number; y: number; w: number; h: number }

function PdfPage({ doc, n, zoom, hits, current }: { doc: PdfDoc; n: number; zoom: number; hits: Hit[]; current: Hit | null }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [ratio, setRatio] = useState(11 / 8.5);
  useEffect(() => {
    let task: { cancel(): void; promise: Promise<void> } | null = null, dead = false;
    doc.getPage(n).then((page) => {
      if (dead) return;
      const base = page.getViewport({ scale: 1 });
      setRatio(base.height / base.width);
      const viewport = page.getViewport({ scale: (PAGE_W * zoom / base.width) * devicePixelRatio });
      const c = canvas.current!;
      c.width = Math.floor(viewport.width); c.height = Math.floor(viewport.height);
      task = page.render({ canvas: c, viewport });
      task.promise.catch(() => {});
    });
    return () => { dead = true; task?.cancel(); };
  }, [doc, n, zoom]);
  const w = PAGE_W * zoom;
  return (
    <div className="lg-page" style={{ width: w, height: w * ratio }}>
      <canvas ref={canvas} style={{ width: w, height: w * ratio }} aria-label={`Page ${n}`} />
      {hits.map((h, i) => (
        <mark key={i} className="lg-hit" data-current={h === current || undefined} id={h === current ? "lg-hit-current" : undefined}
          style={{ left: `${h.x * 100}%`, top: `${h.y * 100}%`, width: `${h.w * 100}%`, height: `${h.h * 100}%` }} />
      ))}
    </div>
  );
}

/** Text of a PDF, one string per line (for Find and Compare). */
async function pdfLines(doc: PdfDoc) {
  const out: { page: number; text: string; x: number; y: number; w: number; h: number }[] = [];
  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n);
    const vp = page.getViewport({ scale: 1 });
    const tc = await page.getTextContent();
    for (const it of tc.items as { str: string; transform: number[]; width: number; height: number }[]) {
      if (!it.str?.trim()) continue;
      const [x, y] = [it.transform[4], it.transform[5]];
      out.push({ page: n, text: it.str, x: x / vp.width, y: 1 - (y + it.height) / vp.height, w: it.width / vp.width, h: Math.max(it.height, 8) / vp.height });
    }
  }
  return out;
}

export function DocPreview({ doc }: { doc: LegislationDocument }) {
  const [pdf, setPdf] = useState<PdfDoc | null>(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(0);
  const [q, setQ] = useState("");
  const [lines, setLines] = useState<Awaited<ReturnType<typeof pdfLines>> | null>(null);
  const [at, setAt] = useState(0);
  const find = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let dead = false, task: import("pdfjs-dist").PDFDocumentLoadingTask | null = null;
    setPdf(null); setFailed(false); setLines(null); setAt(0);
    loadPdfjs().then((m) => {
      if (dead) return;
      task = m.getDocument({ url: doc.url });
      return task.promise.then((d) => { if (!dead) setPdf(d); });
    }).catch(() => !dead && setFailed(true));
    return () => { dead = true; task?.destroy(); };
  }, [doc.url]);
  useEffect(() => {
    if (!pdf || !q || lines) return;
    let dead = false; // another document opened meanwhile: drop this one's text
    void pdfLines(pdf).then((l) => { if (!dead) setLines(l); }).catch(() => { if (!dead) setLines([]); });
    return () => { dead = true; };
  }, [pdf, q, lines]);
  // ⌘K focuses Find in document while this tab is open
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); e.stopPropagation(); find.current?.focus(); } };
    addEventListener("keydown", onKey, true);
    return () => removeEventListener("keydown", onKey, true);
  }, []);

  const hits = useMemo<Hit[]>(() => {
    const t = q.trim().toLowerCase();
    if (!t || !lines) return [];
    return lines.filter((l) => l.text.toLowerCase().includes(t)).map((l) => ({ page: l.page, x: l.x, y: l.y, w: l.w, h: l.h }));
  }, [q, lines]);
  const current = hits.length ? hits[Math.min(at, hits.length - 1)] : null;
  const jump = (d: number) => {
    if (!hits.length) return;
    setAt((i) => (i + d + hits.length) % hits.length);
    requestAnimationFrame(() => document.getElementById("lg-hit-current")?.scrollIntoView({ block: "center", behavior: prefersReducedMotion() ? "auto" : "smooth" }));
  };

  return (
    <section className="lg-preview" aria-label={`Preview of ${doc.name}`}>
      <div className="lg-preview__bar">
        <label className="lg-find">
          <Search size={15} aria-hidden />
          <input ref={find} type="search" placeholder="Find in document" aria-label="Find in document" value={q}
            onChange={(e) => { setQ(e.target.value); setAt(0); }}
            onKeyDown={(e) => { if (e.key === "ArrowDown" || e.key === "Enter") { e.preventDefault(); jump(1); } else if (e.key === "ArrowUp") { e.preventDefault(); jump(-1); } }} />
          {q && hits.length > 0 ? <span className="lg-find__n">{Math.min(at, hits.length - 1) + 1} / {hits.length}</span> : <kbd>⌘K</kbd>}
        </label>
        <span className="spacer" />
        <button className="icon-btn" aria-label="Zoom in" onClick={() => setZoom((z) => (z + 1) % ZOOMS.length)}><ZoomIn size={16} /></button>
        <a className="icon-btn" href={doc.url} download aria-label="Download"><Download size={16} /></a>
        <a className="btn" href={doc.clerkUrl ?? doc.url} target="_blank" rel="noreferrer"><ExternalLink size={14} aria-hidden />City Clerk original</a>
      </div>
      <div className="lg-preview__pages" tabIndex={0} role="region" aria-label={`${doc.name}, pages`}>
        {failed ? <p className="lg-preview__error">This file can't be previewed. Download it to read it.</p>
          : pdf ? Array.from({ length: pdf.numPages }, (_, i) => (
            <PdfPage key={i} doc={pdf} n={i + 1} zoom={ZOOMS[zoom]} hits={hits.filter((h) => h.page === i + 1)} current={current} />
          )) : <div className="lg-page" style={{ width: PAGE_W, height: PAGE_W * 11 / 8.5 }} />}
      </div>
    </section>
  );
}

/* ---------------- Compare versions (41 card 11) ---------------- */

async function textOf(d: LegislationDocument): Promise<string[]> {
  if (d.textUrl) {
    const r = await fetch(d.textUrl, { credentials: "include" });
    if (r.ok) return (await r.text()).split(/\r?\n/);
  }
  const m = await loadPdfjs();
  const doc = await m.getDocument({ url: d.url }).promise;
  const lines: string[] = [];
  for (let n = 1; n <= doc.numPages; n++) {
    const tc = await (await doc.getPage(n)).getTextContent();
    let y: number | null = null, cur = "";
    for (const it of tc.items as { str: string; transform: number[]; hasEOL?: boolean }[]) {
      if (y !== null && Math.abs(it.transform[5] - y) > 2) { lines.push(cur); cur = ""; }
      cur += it.str; y = it.transform[5];
      if (it.hasEOL) { lines.push(cur); cur = ""; y = null; }
    }
    if (cur) lines.push(cur);
  }
  return lines.map((l) => l.trim()).filter(Boolean);
}

type Op = { kind: "same" | "del" | "add" | "change"; a?: string; b?: string };

/** Line diff (LCS); adjacent delete + add pairs become one "change" row so both sides line up. */
export function diffLines(a: string[], b: string[]): Op[] {
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const ops: Op[] = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { ops.push({ kind: "same", a: a[i], b: b[j] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) ops.push({ kind: "del", a: a[i++] });
    else ops.push({ kind: "add", b: b[j++] });
  }
  while (i < n) ops.push({ kind: "del", a: a[i++] });
  while (j < m) ops.push({ kind: "add", b: b[j++] });
  const out: Op[] = [];
  for (const o of ops) {
    const prev = out[out.length - 1];
    if (o.kind === "add" && prev?.kind === "del") { out[out.length - 1] = { kind: "change", a: prev.a, b: o.b }; continue; }
    out.push(o);
  }
  return out;
}

const day = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

export function ComparePanel({ a, b, onClose }: { a: LegislationDocument; b: LegislationDocument; onClose: () => void }) {
  const [ops, setOps] = useState<Op[] | null>(null);
  const [failed, setFailed] = useState(false);
  const left = useRef<HTMLDivElement>(null);
  const right = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);
  const [at, setAt] = useState(-1);
  useEffect(() => {
    let dead = false;
    Promise.all([textOf(a), textOf(b)]).then(([x, y]) => { if (!dead) setOps(diffLines(x.slice(0, 3000), y.slice(0, 3000))); }).catch(() => !dead && setFailed(true));
    return () => { dead = true; };
  }, [a, b]);
  const changes = useMemo(() => (ops ?? []).map((o, i) => (o.kind === "same" ? -1 : i)).filter((i) => i >= 0)
    .filter((i, k, arr) => k === 0 || arr[k - 1] !== i - 1), [ops]);

  // Synced scroll (0ms): whichever side scrolls drives the other.
  const onScroll = (from: HTMLDivElement | null, to: HTMLDivElement | null) => {
    if (!from || !to || syncing.current) return;
    syncing.current = true; to.scrollTop = from.scrollTop;
    requestAnimationFrame(() => { syncing.current = false; });
  };
  const jump = useCallback((d: number) => {
    if (!changes.length) return;
    const next = (at + d + changes.length) % changes.length;
    setAt(next);
    for (const side of [left.current, right.current]) {
      const el = side?.querySelector<HTMLElement>(`[data-op="${changes[next]}"]`);
      if (!el || !side) continue;
      // Measured against the scrolling pane itself (offsetTop is relative to a further ancestor).
      side.scrollTo({ top: el.getBoundingClientRect().top - side.getBoundingClientRect().top + side.scrollTop - side.clientHeight / 3, behavior: prefersReducedMotion() ? "auto" : "smooth" });
      if (!prefersReducedMotion()) el.animate([{ background: "var(--highlight)" }, { background: "transparent" }], { duration: recipes.changeJump.flashMs });
    }
  }, [at, changes]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e) || e.metaKey || e.ctrlKey) return;
      if (e.key === "n") { e.preventDefault(); jump(1); } else if (e.key === "p") { e.preventDefault(); jump(-1); } else if (e.key === "Escape") onClose();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [jump, onClose]);

  const col = (side: "a" | "b", ref: React.RefObject<HTMLDivElement | null>, other: React.RefObject<HTMLDivElement | null>, d: LegislationDocument) => (
    <div className="lg-compare__col">
      <header>{d.version?.label} · {d.version ? day(d.version.date) : ""}</header>
      <div className="lg-compare__text" ref={ref} onScroll={() => onScroll(ref.current, other.current)}>
        {(ops ?? []).map((o, i) => {
          const t = side === "a" ? o.a : o.b;
          const k = o.kind === "same" ? "same" : side === "a" ? (o.kind === "add" ? "gap" : "del") : (o.kind === "del" ? "gap" : "add");
          return <p key={i} data-op={i} data-k={k}>{t ?? " "}</p>;
        })}
      </div>
    </div>
  );
  return (
    <section className="lg-compare" aria-label="Compare versions">
      <div className="lg-compare__bar">
        <span className="lg-compare__keys"><kbd>n</kbd><kbd>p</kbd>{changes.length ? ` ${at < 0 ? 0 : at + 1} / ${changes.length}` : ""}</span>
        <span className="spacer" />
        <button className="icon-btn" aria-label="Close compare" onClick={onClose}><X size={16} /></button>
      </div>
      {failed ? <p className="lg-preview__error">This file can't be previewed. Download it to read it.</p> : (
        <div className="lg-compare__cols">
          {col("a", left, right, a)}
          {col("b", right, left, b)}
          <div className="lg-compare__map" aria-hidden>
            {ops && changes.map((i) => <span key={i} data-k={ops[i].kind} style={{ top: `${(i / Math.max(1, ops.length)) * 100}%` }} />)}
          </div>
        </div>
      )}
    </section>
  );
}
